import { useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Monitor, AlertTriangle, CheckSquare, Bot, FileText, CreditCard, Settings, ScrollText, PlusCircle, Workflow, LogOut, Menu, X, Shield, Building2, Users, Cog, ListChecks, Globe } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { Logo, Loading } from "@/components/common";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PORTAL_NAV = [
  ["Dashboard", "/portal", LayoutDashboard, () => true, true],
  ["Devices", "/portal/devices", Monitor, () => true],
  ["Enroll device", "/portal/enroll", PlusCircle, can.manage],
  ["Incidents", "/portal/incidents", AlertTriangle, () => true],
  ["Approvals", "/portal/approvals", CheckSquare, () => true],
  ["Automation", "/portal/automation", Workflow, () => true],
  ["AI Assistant", "/portal/assistant", Bot, (r) => r !== "auditor"],
  ["Reports", "/portal/reports", FileText, can.export],
  ["Audit log", "/portal/audit", ScrollText, can.audit],
  ["Billing", "/portal/billing", CreditCard, () => true],
  ["Settings", "/portal/settings", Settings, () => true],
];
const ADMIN_NAV = [
  ["Master dashboard", "/admin/master", Globe],
  ["Control center", "/admin", Shield, null, true],
  ["Organizations", "/admin/orgs", Building2],
  ["Plans & pricing", "/admin/plans", ListChecks],
  ["Users", "/admin/users", Users],
  ["Audit events", "/admin/audit", ScrollText],
  ["Platform settings", "/admin/system", Cog],
];

function NavItems({ items, role, onClick }) {
  return items.filter(([, , , f]) => !f || f(role)).map(([l, p, I, , end]) => (
    <NavLink key={p} to={p} end={end} onClick={onClick} data-testid={`sidebar-${l.toLowerCase().replaceAll(" ", "-").replaceAll("&", "and")}-link`}
      className={({ isActive }) => `flex items-center gap-3 px-4 py-2 text-sm border-l-2 transition-colors ${isActive ? "border-red-600 text-white bg-white/5" : "border-transparent text-gray-400 hover:text-white"}`}>
      <I className="w-4 h-4" />{l}
    </NavLink>
  ));
}

export default function AppLayout({ admin = false }) {
  const { session, user, logout, orgId, switchOrg, membership, role, isSuper } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  if (session === null) return <div className="p-10"><Loading /></div>;
  if (session === false) return <Navigate to="/login" replace />;
  if (admin && !isSuper) return <Navigate to="/portal" replace />;
  const resend = async () => {
    await api.post("/auth/resend-verification").then(() => toast.success("Verification email sent")).catch(() => toast.error("Could not send now"));
  };
  const sidebar = (mobile) => (
    <aside className="w-64 shrink-0 bg-gray-900 text-gray-300 flex flex-col h-full" data-testid={mobile ? "app-sidebar-mobile" : "app-sidebar"}>
      <div className="h-16 flex items-center px-4 border-b border-gray-800"><Link to="/"><Logo dark /></Link></div>
      {!admin && session.memberships.length > 0 && (
        <div className="p-4 border-b border-gray-800">
          <label className="text-[10px] uppercase tracking-widest text-gray-500">Organization</label>
          <select value={orgId} onChange={(e) => { switchOrg(e.target.value); nav("/portal"); window.location.reload(); }}
            className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm px-2 py-1.5" data-testid="org-switcher-select">
            {session.memberships.map((m) => <option key={m.id} value={m.id}>{m.name}{m.via ? " (client)" : ""}</option>)}
          </select>
          <div className="text-[11px] text-gray-500 mt-1" data-testid="current-role-label">Role: {role?.replace("_", " ")}</div>
        </div>
      )}
      <nav className="flex-1 py-3 overflow-y-auto space-y-0.5">
        <NavItems items={admin ? ADMIN_NAV : PORTAL_NAV} role={role} onClick={() => setOpen(false)} />
        {!admin && membership?.type === "msp" && <NavItems items={[["MSP clients", "/portal/clients", Building2]]} role={role} />}
      </nav>
      <div className="border-t border-gray-800 p-3 space-y-1">
        {isSuper && <Link to={admin ? "/portal" : "/admin"} className="block text-xs text-gray-400 hover:text-white px-1" data-testid="switch-portal-link">{admin ? "← Customer portal" : "Super admin control center →"}</Link>}
        <div className="text-xs text-gray-500 px-1 truncate" data-testid="current-user-email">{user?.email}</div>
        <button onClick={async () => { await logout(); nav("/login"); }} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white px-1" data-testid="logout-btn"><LogOut className="w-4 h-4" />Sign out</button>
      </div>
    </aside>
  );
  return (
    <div className="h-screen flex bg-white">
      <div className="hidden lg:block">{sidebar(false)}</div>
      {open && <div className="fixed inset-0 z-50 flex lg:hidden"><div className="h-full">{sidebar(true)}</div><div className="flex-1 bg-black/50" onClick={() => setOpen(false)} /></div>}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="lg:hidden h-14 bg-gray-900 flex items-center justify-between px-4">
          <Logo dark /><button onClick={() => setOpen(!open)} className="text-white" data-testid="mobile-sidebar-toggle">{open ? <X /> : <Menu />}</button>
        </div>
        {session.mfa_setup_required && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-sm px-6 py-2" data-testid="mfa-required-banner">
            MFA is required for administrator actions. <Link to="/mfa-setup" className="underline font-semibold">Enable now</Link>
          </div>
        )}
        {!user.email_verified && (
          <div className="bg-gray-50 border-b border-gray-200 text-gray-700 text-sm px-6 py-2" data-testid="verify-email-banner">
            Verify your email to enable device enrollment. <button onClick={resend} className="underline font-semibold" data-testid="resend-verification-btn">Resend email</button>
          </div>
        )}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[1400px] mx-auto p-6 md:p-10">
            {!admin && !session.memberships.length ? (
              <div className="card-flat p-10" data-testid="no-org-state">You are not a member of any customer organization. {isSuper && <Link to="/admin" className="text-red-600 underline">Open the control center</Link>}</div>
            ) : <Outlet />}
          </div>
        </main>
      </div>
    </div>
  );
}
